import { SELECTION_RADIUS } from "@/tools/pointer-move-preview-handler";
import { Pivot, useSvg } from "@faro-lotv/app-component-toolbox";
import { ImagePoint, InfiniteLine, TiledPano } from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import { RefObject, useEffect, useRef, useState, type JSX } from "react";
import {
  BufferGeometry,
  Camera,
  CircleGeometry,
  EdgesGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Points,
  PointsMaterial,
  Uint8BufferAttribute,
  Vector3,
} from "three";
import {
  LineMaterial,
  LineSegments2,
  LineSegmentsGeometry,
} from "three-stdlib";

export type PanoPoint = {
  /** Pano object */
  pano: TiledPano;

  /** A pixel point on the pano depth image */
  depthImagePoint: ImagePoint;
};

type PreviewFeatureRendererProps = {
  /** Position used to detect features at */
  position: Vector3;

  /** The normal direction detected at the current pointer position */
  normal: Vector3;

  /** The edge detected at the current pointer position */
  edge?: InfiniteLine;

  /** The corner detected at the current pointer position */
  corner?: Vector3;
};

// cached temporary objects for avoiding repleated allocation
const tmpObjs = {
  zAxis: new Vector3(0, 0, 1),
  refDir: new Vector3(),
  yAxis: new Vector3(0, 1, 0),
};

/** Name assigned to the preview feature object in the scene graph */
export const PREVIEW_FEATURE_NAME = "PreviewFeature";

/**
 * @returns Renderer for geometric features detected at a position of a model
 */
export function PreviewFeatureRenderer({
  position,
  normal,
  edge,
  corner,
}: PreviewFeatureRendererProps): JSX.Element | null {
  const { camera } = useThree();
  const { planeObj, planeGroupRef } = useNormalPlane();
  const { edgeObj, edgeGroupRef } = useEdgeLine();
  const { cornerObj, cornerGroupRef } = useCorner();

  useEffect(() => {
    updatePlane(planeGroupRef, position, normal, camera);
    updateEdge(edgeGroupRef, edge, !corner);
    updateCorner(cornerGroupRef, corner);
  }, [
    camera,
    corner,
    cornerGroupRef,
    edge,
    edgeGroupRef,
    normal,
    planeGroupRef,
    position,
  ]);

  return (
    <group>
      <group ref={planeGroupRef}>
        <primitive object={planeObj} />
      </group>
      <group ref={edgeGroupRef}>
        <primitive object={edgeObj} />
      </group>
      <group ref={cornerGroupRef}>
        <primitive object={cornerObj} />
      </group>
    </group>
  );
}

function useNormalPlane(): {
  planeObj: Object3D;
  planeGroupRef: RefObject<Group | null>;
} {
  const [planeObj] = useState<Object3D>(() => {
    const discGeom = new CircleGeometry(SELECTION_RADIUS);
    const disc = new Mesh(
      discGeom,
      new MeshBasicMaterial({
        color: "white",
        transparent: true,
        opacity: 0.25,
      }),
    );
    disc.name = PREVIEW_FEATURE_NAME;

    const borderGeom = new EdgesGeometry(discGeom);
    const border = new LineSegments(
      borderGeom,
      new LineBasicMaterial({
        color: "black",
        transparent: true,
        opacity: 0.2,
      }),
    );
    border.name = PREVIEW_FEATURE_NAME;

    const group = new Group();
    group.add(disc);
    group.add(border);
    return group;
  });
  const planeGroupRef = useRef<Group>(null);
  return { planeObj, planeGroupRef };
}

function updatePlane(
  planeRef: RefObject<Group | null>,
  position: Vector3,
  planeNormal: Vector3 | undefined,
  camera: Camera,
): void {
  if (!planeRef.current) return;

  if (!planeNormal) {
    // hide the plane if no normal is found
    planeRef.current.visible = false;
    return;
  }

  // Use direction from the position to the camera position as reference to make sure
  // the normal is always pointing towards the camera so the plane is always visible
  camera.getWorldPosition(tmpObjs.refDir);
  tmpObjs.refDir.sub(position);
  if (tmpObjs.refDir.dot(planeNormal) < 0) {
    planeNormal.negate();
  }

  // offset the plane 1 inch along normal, so it won't be blocked by points
  const offset = 0.0254;
  planeRef.current.position.copy(position);
  planeRef.current.position.addScaledVector(planeNormal, offset);

  planeRef.current.quaternion.setFromUnitVectors(tmpObjs.zAxis, planeNormal);
  planeRef.current.updateWorldMatrix(true, true);
}

// Constants define the color and width of the edge line
const EDGE_COLOR = 0xff7f00;
const EDGE_DEACTIVE_COLOR = 0xffffff;
const EDGE_WIDTH = 5;
const EDGE_DEACTIVE_WIDTH = 2;

// Render corner after the edge line, making sure the edge line is always behind corner dot
const EDGE_RENDER_ORDER = 10;
const CONER_RENDER_ORDER = 20;

function useEdgeLine(): {
  edgeObj: Object3D;
  edgeGroupRef: RefObject<Group | null>;
} {
  const [edgeObj] = useState<Object3D>(() => {
    const positions = [0, -SELECTION_RADIUS, 0.0, 0, SELECTION_RADIUS, 0.0];
    const lineGeom = new LineSegmentsGeometry().setPositions(positions);
    const edge = new LineSegments2(
      lineGeom,
      new LineMaterial({
        color: EDGE_COLOR,
        linewidth: EDGE_WIDTH,
        depthTest: false,
        opacity: 0.6,
        transparent: true,
      }),
    );
    edge.name = PREVIEW_FEATURE_NAME;
    edge.renderOrder = EDGE_RENDER_ORDER;
    return edge;
  });
  const edgeGroupRef = useRef<Group>(null);
  return { edgeObj, edgeGroupRef };
}

function updateEdge(
  edgeRef: RefObject<Group | null>,
  edge: InfiniteLine | undefined,
  active: boolean,
): void {
  if (!edgeRef.current) return;

  if (!edge) {
    // hide the edge if no edge is found
    edgeRef.current.visible = false;
    return;
  }
  edgeRef.current.visible = true;
  edgeRef.current.position.copy(edge.point);
  edgeRef.current.quaternion.setFromUnitVectors(tmpObjs.yAxis, edge.direction);
  edgeRef.current.updateWorldMatrix(true, true);

  const edgeObj = edgeRef.current.children[0];
  if (edgeObj instanceof LineSegments2) {
    edgeObj.material.color.set(active ? EDGE_COLOR : EDGE_DEACTIVE_COLOR);
    edgeObj.material.linewidth = active ? EDGE_WIDTH : EDGE_DEACTIVE_WIDTH;
  }
}

function useCorner(): {
  cornerObj: Object3D;
  cornerGroupRef: RefObject<Group | null>;
} {
  const pointTexture = useSvg(Pivot, 256, 256);

  const [cornerObj] = useState<Object3D>(() => {
    const geometry = new BufferGeometry().setAttribute(
      "position",
      new Uint8BufferAttribute([0, 0, 0], 3, false),
    );

    const corner = new Points(
      geometry,
      new PointsMaterial({
        depthTest: false,
        size: 16,
        sizeAttenuation: false,
        transparent: true,
        opacity: 0.8,
        map: pointTexture,
      }),
    );
    corner.name = PREVIEW_FEATURE_NAME;
    corner.renderOrder = CONER_RENDER_ORDER;
    return corner;
  });
  const cornerGroupRef = useRef<Group>(null);
  return { cornerObj, cornerGroupRef };
}

function updateCorner(
  cornerRef: RefObject<Group | null>,
  position: Vector3 | undefined,
): void {
  if (!cornerRef.current) return;

  if (!position) {
    // hide the corner if no edge is found
    cornerRef.current.visible = false;
    return;
  }

  cornerRef.current.visible = true;
  cornerRef.current.position.copy(position);
  cornerRef.current.updateWorldMatrix(true, true);
}
